'use client';

/* eslint-disable @next/next/no-img-element */
import Image from 'next/image';
import { FC } from 'react';

import addAdminModalImage from '@/../public/_n/images/add-admin-modal-example.png';
import addAdminSectionImage from '@/../public/_n/images/sftp-add-admin-section.png';
import emailRecipientsImage from '@/../public/_n/images/sftp-email-recipients-example.png';
import sshKeyInstructionsImage from '@/../public/_n/images/sftp-ssh-key-instructions.png';
import srcLoader from '@/utils/ui/imageLoader';

import {
	ApiCode,
	Content,
	ContentWrapper,
	Header,
	InlineCode,
	SectionBody,
	SectionHeader,
	Title,
	Updated,
} from '../styles';

const SftpInstructions: FC = () => {
	return (
		<div>
			<Header>
				<img src="/_n/images/calm_square_logo.png" alt="Calm Logo" />
				<Title>SFTP Instructions</Title>
				<Updated>Last Updated: July 23, 2025</Updated>
			</Header>

			<ContentWrapper>
				<Content>
					<SectionHeader>Overview</SectionHeader>
					<SectionBody>
						<p>
							These instructions describe the steps to configure a SFTP connection to the Calm SFTP server
							with the purpose of automating eligibility file uploads.
						</p>
						<p>
							<strong>Steps</strong>
						</p>
						<ol>
							<li>
								<a href="#eligibility-file">Prepare Eligibility File</a>: An eligibility file is a list of
								users who are eligible to redeem the Calm benefit. This file must contain a unique identifier
								for each user that the end user can enter into the app to uniquely identify themselves and
								gain access to Calm.
							</li>
							<li>
								<a href="#sftp-notifications">Set SFTP Status Notifications</a>: Assign contact email
								addresses to receive updates for file successful and failed file transfer.
							</li>
							<li>
								<a href="#contact">Configure technical contact in Partner Portal</a>: Enter the email address
								for your technical contact by clicking the &quot;Add new admin&quot; button. A modal will
								display and &quot;First Name&quot;, &quot;Last Name&quot;, and &quot;Email&quot; are required.
								The technical contact will receive instructions to access the Partner Portal and finish the
								SFTP configuration setup.
							</li>
							<li>
								<a href="#ssh-key">Generate SSH key</a>: SSH keys are used for secure authentication and must
								be in OpenSSH format. Generate an SSH key pair (public and private key) using a tool like
								OpenSSH, PuTTYgen, or ssh-keygen. Share the public key with the SFTP server administrator and
								keep the private key secure.
							</li>
							<li>
								<a href="#exchange-keys">Share public SSH key with Calm</a>: Provide Calm with your public key
								in the Calm Partner Portal. We will configure our server to recognize your public key for
								authentication purposes.
							</li>
							<li>
								<a href="#test-connection">Test the connection</a>: Use SFTP client software such as
								FileZilla, WinSCP, or Cyberduck to test the SFTP connection to the server. Enter the
								server&apos;s hostname or IP address, port number, your username, and select the private key
								file for authentication. Verify that the connection works properly.
							</li>
							<li>
								<a href="#configure-sync">Configure automated file syncing</a>: Configure your Human Resource
								Information System (HRIS), tool, or script to automate these operations. We recommend syncing
								your file at least monthly.
							</li>
						</ol>
						<p>
							<strong>Instructions for common automated file syncing tools</strong>
						</p>
						<ul>
							<li>
								<a
									href="https://docs.google.com/document/d/1ApvFWc9lwHAxNPaAfDNX_MLLfGJht7KXfar3MMKJpuU/edit?usp=sharing"
									target="_blank"
									rel="noopener noreferrer"
								>
									Workday Instructions
								</a>
							</li>
							<li>
								<a
									href="https://help.sap.com/docs/cloud-integration/sap-cloud-integration/setting-up-outbound-sftp-connections"
									target="_blank"
									rel="noopener noreferrer"
								>
									SAP: Setting Up Outbound SFTP Connections
								</a>
							</li>
							<li>
								<a
									href="https://cloud.google.com/integration-connectors/docs/connectors/sftp/configure"
									target="_blank"
									rel="noopener noreferrer"
								>
									Google SFTP Connector
								</a>
							</li>
						</ul>
						<p>
							<strong>Troubleshooting</strong>
						</p>
						<ul>
							<li>
								<a href="#sftp-connection-errors">SFTP connection errors</a>: Fix common problems connecting
								to the Calm SFTP server.
							</li>
							<li>
								<a href="#sftp-upload-errors">SFTP upload errors</a>: Fix common problems uploading the file.
							</li>
						</ul>
					</SectionBody>
					<SectionHeader id="eligibility-file">
						Note on Transitioning from Calm Business SFTP to Calm Health SFTP
					</SectionHeader>
					<SectionBody>
						<p>
							Each Calm Business and Calm Health program has its own Partner Portal and issues a unique SFTP
							username for eligibility file uploads. The SFTP hostname is the same (sftp.ws.calm.com), but
							each program’s file must be sent to the correct username and portal. When setting up Calm
							Health, we recommend generating a new SSH key for that SFTP connection.
						</p>

						<p>
							During a transition period from Calm Business to Calm Health, it is customary for a customer to
							send both a Calm Business and Calm Health Eligibility File in parallel. These are two separate
							SFTP eligibility files, capturing Eligibility for each program. Calm Business files follow a
							simpler format, typically including a unique identifier and/or email, alongside Segment data,
							while Calm Health files must follow our{' '}
							<a
								target="_blank"
								href="https://docs.google.com/spreadsheets/d/1yoZbNpwnDjV9ijSS82CAobeu4V9N10CA7gTy55joZgM/edit?usp=sharing"
							>
								Default Eligibility File Format
							</a>
							.
						</p>
						<p>
							When ready to stop syncing for Calm Business, customers can disable automation or clear out the
							SSH key on the Calm Business side; Calm does not need to take any action. Note that Calm Health
							SFTP files often include PHI/HITRUST data, so Calm employees must follow HITRUST protocols for
							troubleshooting (never email files, use the Portal, and screenshare if needed).
						</p>
					</SectionBody>
					<SectionHeader id="eligibility-file">1. Prepare Eligibility File</SectionHeader>
					<SectionBody>
						<p>
							Calm Health eligibility files are formatted differently from Calm Business eligibility files.
							Follow the instructions for your Calm product.
						</p>

						<p>
							<strong>Calm Health</strong>
						</p>
						<p>
							The Calm Health product has its own{' '}
							<a href="https://info.calm.com/rs/541-LYF-023/images/Eligibility_File_Setup_Guide_CalmHealth.pdf">
								Eligibility File Setup Guide
							</a>
							. For details on formatting a Calm Health eligibility file, please reference{' '}
							<a href="https://info.calm.com/rs/541-LYF-023/images/Eligibility_File_Setup_Guide_CalmHealth.pdf#page=3">
								&apos;Setting up the Eligibility File&apos;
							</a>{' '}
							on page 3.
						</p>

						<p>
							<strong>Calm Business</strong>
						</p>
						<p>Calm Business eligibility files:</p>
						<ul>
							<li>
								must include all eligible members. Eligible members that are not included in the file will
								lose access to the Calm product.
							</li>
							<li>must be in CSV file format (file ends in .csv)</li>
							<li>can be named anything you want, but Calm recommends unique names for each file</li>
							<li>
								can be sent at the frequency and timing of your choice. Calm recommends sending a file at
								least monthly.
							</li>
							<li>
								must quote values that contain commas or apostrophes. For example, the value{' '}
								<InlineCode>Vice President, Southwest Region</InlineCode>, must be enclosed in quotes:
								<InlineCode>&quot;Vice President, Southwest Region&quot;</InlineCode>. The same goes for the
								name <InlineCode>O&apos;Donnell</InlineCode>, which must also be enclosed in quotes:{' '}
								<InlineCode>&quot;O&apos;Donnell&quot;</InlineCode>.
							</li>
						</ul>

						<p>
							At a minimum, the eligibility file must include a column with a unique identifier for each user.
							This unique identifier can be an email address, employee ID, or any other unique value that the
							end user can enter into the app to uniquely identify themselves and gain access to Calm. Each
							row should have only one identifier.
						</p>

						<p>
							<strong>Enable segmented reporting</strong>
						</p>

						<p>
							Segmented reporting summarizes data across categories of people such as gender, job title, and
							location. Segments containing less than 10 signups will not display in reporting for privacy
							reasons.
						</p>

						<p>
							Segmented reporting can be enabled by adding up to three additional columns in the eligibility
							file, for a maximum total of four columns. Eligibility files with segmented reporting must
							include a header row that labels the columns of data. The first column must contain the unique
							identifiers. The second, third, and fourth columns are for the segments (categories) that
							describe each user.
						</p>
					</SectionBody>
					<SectionHeader id="sftp-notifications">
						2. Designate recipients for SFTP status notifications
					</SectionHeader>
					<SectionBody>
						<p>
							Calm can send file transfer status emails to your preferred contacts. Enter the email addresses
							for these contacts separated by a comma (example: jane.doe@company.com, john.doe@company.com).
						</p>
						<Image
							src={emailRecipientsImage}
							alt="SFTP Email Recipients Example"
							height={330}
							loader={srcLoader}
						/>
					</SectionBody>
					<SectionHeader id="contact">3. Configure technical contact in Partner Portal</SectionHeader>
					<SectionBody>
						<p>
							Enter the email address for your technical contact by clicking “Add new admin” button. A modal
							will display and First Name, Last Name, Email are required. The technical contact will receive
							instructions to access the Partner Portal and finish the SFTP configuration setup.
						</p>
						<Image src={addAdminSectionImage} alt="Add Admin Section" height={325} loader={srcLoader} />
						<Image src={addAdminModalImage} alt="Add Admin Modal Example" height={500} loader={srcLoader} />
					</SectionBody>

					<SectionHeader id="ssh-key">4. Generate SSH key</SectionHeader>
					<SectionBody>
						<p>
							Please ensure all public SSH keys submitted in Partner Portal are in OpenSSH format. Below are
							instructions on how to generate an SSH key.
						</p>
						<p>
							<strong>Mac/Linux systems</strong>
						</p>
						<ol>
							<li>
								Run the following command in a terminal window to generate an SSH key in OpenSSH format:{' '}
								<InlineCode>ssh-keygen -t ed25519 -C &apos;your_email@example.com&apos;</InlineCode> or{' '}
								<InlineCode>ssh-keygen -t ed25519</InlineCode>
							</li>
							<li>
								Create a passphrase or decline when prompted. The passphrase is optional. If you choose to
								create a passphrase, you wil be required to enter that passphrase every time you connect with
								SSH to Calm&apos;s SFTP server.
							</li>
							<li>
								Type <InlineCode>open ~/.ssh/</InlineCode> to open the folder where your SSH keys are stored.
								Inside that folder is a file named <InlineCode>id_rsa.pub</InlineCode>. This is the public SSH
								key that you will enter into the Calm Partner Portal
							</li>
						</ol>

						<p>
							<strong>Windows systems</strong>
						</p>
						<ol>
							<li>Download PuTTY and PuTTYGen to generate your OpenSSH RSA key on Windows</li>
							<li>Select &quot;EdDSA&quot; as the type of key to generate</li>
							<li>Enter &quot;Ed25519 (255bits)&quot; for the number of bits</li>
							<li>
								Click &quot;Generate&quot; and follow PuTTY&apos;s instructions to create the key. Wait for
								key generation to complete.
							</li>
							<li>
								Click &quot;Save public key&quot; and then &quot;Save private key&quot; to save both as a file
								on your computer — the private key is what you&apos;ll use to authenticate and the public key
								is what you&apos;ll enter into the Calm Partner Portal.
							</li>
							<li>
								Copy the contents of the field &quot;Public key&quot; for pasting into OpenSSH authorized_keys
								file from PuTTY. Now you&apos;re ready to enter your copied public key into the Calm Partner
								Portal.
							</li>
						</ol>
						<p>
							For more detailed instructions, visit Github&apos;s{' '}
							<a
								href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/generating-a-new-ssh-key-and-adding-it-to-the-ssh-agent"
								target="_blank"
								rel="noopener noreferrer"
							>
								guide on generating a new SSH key
							</a>
						</p>
					</SectionBody>

					<SectionHeader id="exchange-keys">5. Share public SSH key with Calm</SectionHeader>
					<SectionBody>
						<ol>
							<li>Copy the generated public SSH key</li>
							<li>Log into the Calm Partner Portal</li>
							<li>Paste the generated public SSH key into the &quot;SSH key&quot; field</li>
							<li>
								Click the &quot;Save Configuration&quot; button. Your SFTP Username and SFTP URL will be
								displayed
							</li>
						</ol>
						<Image
							src={sshKeyInstructionsImage}
							alt="SSH Key Field"
							width={800}
							height={600}
							loader={srcLoader}
						/>
					</SectionBody>

					<SectionHeader id="test-connection">6. Test connection to Calm SFTP server</SectionHeader>
					<SectionBody>
						<ol>
							<li>
								<p>
									Locate your SFTP URL and username. Select the private key file for authentication that
									corresponds to the public SSH key entered in Calm Partner Portal.
								</p>

								<p>
									Your SFTP URL is listed on the SFTP integration page at{' '}
									<a href="https://partner.calm.com" target="_blank" rel="noopener noreferrer">
										partner.calm.com
									</a>
								</p>
							</li>
							<li>
								<p>
									On the command line from the machine with the private key counterpart to the public SSH key
									you provided Calm, you can use:
								</p>
								<ApiCode>sftp -oHostKeyAlgorithms=+ssh-rsa sftp_username@sftp.ws.calm.com.</ApiCode>
							</li>
						</ol>
					</SectionBody>

					<SectionHeader id="configure-sync">7. Configure automated file syncing</SectionHeader>
					<SectionBody>
						<p>
							There are a variety of Human Resource Information Systems (HRIS), software tools, and scripts
							that can be used to connect to the Calm SFTP server and upload eligibility files.
						</p>
						<p>
							The following configuration settings are required to connect to the Calm SFTP server and upload
							eligibility files:
						</p>
						<ul>
							<li>
								<strong>SFTP Server/Host</strong>: sftp.ws.calm.com
							</li>
							<li>
								<strong>SFTP Port</strong>: 22
							</li>
							<li>
								<strong>SFTP Username</strong>: Provided in Partner Portal after SSH key upload
							</li>
							<li>
								<strong>Directory</strong>: /inbound/eligibility
							</li>
							<li>
								<strong>Authentication</strong>: OpenSSH key (no password)
							</li>
							<li>
								<strong>PGP encryption</strong>: We do not support PGP encryption of eligibility files
							</li>
						</ul>
						<p>
							Consult the documentation of the respective tools for detailed instructions and troubleshooting
							assistance. Here are instructions for a few common tools:
						</p>
						<ul>
							<li>
								<a
									href="https://docs.google.com/document/d/1ApvFWc9lwHAxNPaAfDNX_MLLfGJht7KXfar3MMKJpuU/edit?usp=sharing"
									target="_blank"
									rel="noopener noreferrer"
								>
									Workday Instructions
								</a>
							</li>
							<li>
								<a
									href="https://help.sap.com/docs/cloud-integration/sap-cloud-integration/setting-up-outbound-sftp-connections"
									target="_blank"
									rel="noopener noreferrer"
								>
									SAP: Setting Up Outbound SFTP Connections
								</a>
							</li>
							<li>
								<a
									href="https://cloud.google.com/integration-connectors/docs/connectors/sftp/configure"
									target="_blank"
									rel="noopener noreferrer"
								>
									Google SFTP Connector
								</a>
							</li>
						</ul>
						<p>The command line can also be used to connect to the Calm SFTP server and upload files:</p>
						<ApiCode>
							sftp -oHostKeyAlgorithms=+ssh-rsa your-sftp-username@sftp.ws.calm.com &lt;&lt;&lt; $&apos;put
							/path/to/local/file/csv /inbound/eligibility/any-filename.csv
						</ApiCode>
						<p>
							<b>
								Note that you must upload your file to the <InlineCode>/inbound/eligibility/</InlineCode>{' '}
								folder.
							</b>
						</p>
						<p>
							Attempts to upload the file to other directories, upload folders to that directory, or upload
							files that are not of type CSV will cause errors.
						</p>
						<p>Avoid:</p>
						<ul>
							<li>
								<InlineCode>/inbound/eligibility/any-filename.txt</InlineCode>: incorrect file format
							</li>
							<li>
								<InlineCode>/inbound/eligibility/some-other-folder/any-filename.csv</InlineCode>: nested
								folders
							</li>
							<li>
								<InlineCode>/any-filename.csv</InlineCode>: incorrect remote directory
							</li>
							<li>
								<InlineCode>/inbound/elgibility/any-filename-YYYY-MM-DD.csv</InlineCode>: misspelled directory
							</li>
						</ul>
						<p>Do:</p>
						<ul>
							<li>
								<InlineCode>/inbound/eligibility/any-filename-YYYY-MM-DD.csv</InlineCode>
							</li>
						</ul>
					</SectionBody>

					<SectionHeader id="sftp-upload-errors">SFTP upload errors</SectionHeader>
					<SectionBody>
						<p>
							Calm sends alert emails to the technical contact configured in the Calm Partner Portal whenever
							a file upload fails.
						</p>
						<p>
							<strong>Common user errors</strong>
						</p>
						<ul>
							<li>
								<p>
									<InlineCode>incorrect_file_type</InlineCode> (Calm Business) and{' '}
									<InlineCode>not_csv</InlineCode> (Calm Health) : The uploaded file must end in{' '}
									<InlineCode>.csv</InlineCode> and be a valid CSV file.
								</p>
							</li>
							<li>
								<p>
									<InlineCode>misplaced_file</InlineCode> (Calm Business) or{' '}
									<InlineCode>Unable to create or access resource</InlineCode> (Calm Health): The file was
									uploaded to the incorrect directory. Please double check that you are uploading the file to
									the <InlineCode>/inbound/eligibility/</InlineCode> directory.
								</p>
							</li>
							<li>
								<p>
									<InlineCode>[column name]: [column name] should be in X format</InlineCode> (Calm Health):
									The specified column name is not in the expected format. The message should state what
									format is expected.
								</p>
							</li>
							<li>
								<p>
									<InlineCode>invalid_length</InlineCode> (Calm Health): There are an unexpected number of
									columns in a given row.
								</p>
							</li>
							<li>
								<p>
									<InlineCode>ef_upload_failure</InlineCode> (Calm Business) and{' '}
									<InlineCode>parser_err</InlineCode> (Calm Health): There is a problem with the format of the
									file you are trying to upload. We only accept CSV (comma-separated values) files. Please
									double check that you are uploading a valid CSV file. Some common problems include:
								</p>
								<ul>
									<li>
										Too many columns (Calm Business only): We allow a maximum of one column for the employee
										identifier and three segmentation columns for a total of four columns.
									</li>
									<li>
										Inconsistent column length: Every row must have the same number of columns. This error is
										sometimes caused by values with commas, addressed in the following item.
									</li>
									<li>
										Values with commas: Values that contain commas, for example,{' '}
										<InlineCode>Vice President, Southwest Region</InlineCode>, must be enclosed in quotes:{' '}
										<InlineCode>&quot;Vice President, Southwest Region&quot;</InlineCode>. Commas determine
										where one value ends and the next one begins.
									</li>
									<li>
										Values with apostrophes and quotes: Values that contain apostrophes or quotes, for
										example, <InlineCode>O&apos;Donnell</InlineCode>, must be enclosed in quotes:{' '}
										<InlineCode>&quot;O&apos;Donnell&quot;</InlineCode>. Apostrophes and quotes determine
										where one value ends and the next one begins.
									</li>
								</ul>
							</li>
						</ul>
						<p>
							<strong>Internal errors</strong>
						</p>
						<p>
							The following errors suggest a internal error on Calm’s end. Please contact your Customer
							Success Manager and they will escalate this to our engineering team:
						</p>
						<ul>
							<li>
								<InlineCode>copy_sftp_object_failure</InlineCode>
							</li>
							<li>
								<InlineCode>delete_sftp_object_failure</InlineCode>
							</li>
							<li>
								<InlineCode>incorrect_partner_state_folder</InlineCode>
							</li>
							<li>
								<InlineCode>invalid_sftp_message_event</InlineCode>
							</li>
							<li>
								<InlineCode>missing_sftp_object_in_bucket</InlineCode>
							</li>
							<li>
								<InlineCode>no_records_on_message</InlineCode>
							</li>
							<li>
								<InlineCode>partner_not_eligibility_file_integration_type</InlineCode>
							</li>
							<li>
								<InlineCode>partner_not_found_in_db</InlineCode>
							</li>
						</ul>
					</SectionBody>

					<SectionHeader id="sftp-connection-errors">SFTP connection errors</SectionHeader>
					<SectionBody>
						<p>
							SFTP connection errors indicate problems establishing the SFTP connection. These errors are
							surfaced in the tool you are using to make the connection and do not result in emails to your
							technical contact.
						</p>
						<p>
							<strong>Connection Error: Unable to negotiate error</strong>
						</p>
						<ApiCode>
							Unable to negotiate with X.X.X.X port 22: no matching host key type found. Their offer: ssh-rsa
						</ApiCode>
						<p>
							Specify the host key algorithm as ssh-rsa. Implementation will vary. On the command line it is{' '}
							<InlineCode>-oHostKeyAlgorithms=+ssh-rsa</InlineCode>
						</p>
						<p>
							<strong>Connection Error: Any request for a password.</strong>
						</p>
						<p>
							Double-check your SFTP username to make sure it is correct. Make sure that you have uploaded the
							correct SFTP public key to the Calm Partner Portal and that you are attempting the connection
							from the machine that has the private key counterpart to the public key you shared.
						</p>
						<p>
							<strong>Firewall error</strong>
						</p>
						<p>
							The Calm SFTP server needs to be able to get past your firewall to successfully make a
							connection. If you&apos;re not able to connect to the server, your firewall is likely blocking
							the connection and you may see the following error message:{' '}
							<InlineCode>Something went wrong. Please try again.</InlineCode>
						</p>
						<p>
							Please allowlist the domain for the Calm SFTP server: <InlineCode>sftp.ws.calm.com</InlineCode>.
							Then run the following command on the command line to see if your organization&apos;s firewall
							is still blocking connections:
						</p>
						<ApiCode>
							sftp -v -o &quot;IdentitiesOnly yes&quot; -F/dev/null -i &lt;PRIVATE_KEY_FILE&gt;
							&lt;SFTP-username&gt; @sftp.ws.calm.com
						</ApiCode>
						<p>
							If you&apos;re able to connect successfully, you&apos;re all set! Your firewall settings are
							optimal for a connection.
						</p>
						<p>
							<strong>OpenSSH key error</strong>
						</p>
						<p>
							Ensure you&apos;re using the correct OpenSSH key type, in the correct format. Keys must be RSA
							public keys in the OpenSSH format.
						</p>
						<p>
							If you attempt to submit the wrong SSH key type, you may see the following error message when
							attempting to establish a connection:
							<InlineCode>SSH key must adhere to OpenSSH format</InlineCode>. Here is a valid OpenSSH key:
						</p>
						<ApiCode>
							ssh-ed25519 AAAAC3NzaD1lZDI1NLE8AAAAIF/W74BbqE0lzO3FgxGFuC5YUpw2KBi2mOcXM+b1w/UK
						</ApiCode>
						<p>
							Make sure you provide the Calm server with the public SSH key that corresponds to your private
							key to connect to the Calm SFTP server.
						</p>
						<p>
							<strong>Multiple SSH key error</strong>
						</p>
						<p>
							A single public key must be entered that matches your organization&apos;s single private key. If
							multiple public keys are sent, or you have more than one private key for your public key, you
							may experience the following issues:
						</p>
						<ul>
							<li>
								FileZilla is unable to connect on a Mac because multiple multiple keys are present. To prevent
								FileZilla from using other keys, start the program in the Terminal App using the following
								command:
								<ApiCode>SSH_AUTH_SOCK=&quot;&quot; open /Applications/FileZilla.app</ApiCode>
							</li>
							<li>
								Socket connection closed. Use this command to test whether you can successfully connect:{' '}
								<ApiCode>
									sftp -v -o &quot;IdentitiesOnly yes&quot; -F/dev/null -i &lt;PRIVATE_KEY_FILE&gt;
									&lt;SFTP-username&gt; @sftp.ws.calm.com
								</ApiCode>
							</li>
						</ul>
					</SectionBody>
				</Content>
			</ContentWrapper>
		</div>
	);
};

export default SftpInstructions;
